// pages/social-login/config.ts — the single typed view of the SSO `window.config` surface.
//
// Everything the module reads from runtime config funnels through here so the (untyped, host-injected)
// `window.config` is narrowed in ONE place and the rest of the module stays fully typed (nova forbids
// `any`). Values are delivered helm -> ini.js -> window.config. The global `window.config` type is NOT
// modified: we narrow to this local view, so the app-wide config type stays owned by its area.
//
// The BACKEND is the source of truth for the feature (`automation-micros-wl_owner_hub` sso.md): a
// provider whose `sso.<provider>.clientId` is empty is rejected server-side (`SSO_PROVIDER_NOT_CONFIGURED`).
// The FE flags below only govern whether the button renders; they never carry a secret (client id is public).

/** The identity providers this module can render. No wallets, no magic-link (those are not SSO here). */
export type SsoProvider = 'google' | 'telegram';

/** The ONLY recognised provider keys, as a stable list for ordered parsing + narrowing. */
const RECOGNISED_PROVIDERS: readonly SsoProvider[] = ['google', 'telegram'];

/**
 * The SSO slice of `window.config`. Only the keys this module reads are declared. Everything is
 * optional: a build that never set SSO config must degrade to "render nothing", never throw. The
 * client ids are PUBLIC (Google's is the id_token `aud`; Telegram's is the bot Client ID); the
 * client secrets live only on the backend and are never exposed here.
 */
export interface SsoRuntimeConfig {
  /**
   * Global master switch. Accepts a boolean OR the tenant string-flag convention (`'1'`/`'0'`).
   * Coerce with {@link isMasterEnabled} — a bare truthiness check treats the string `'0'` as ON.
   */
  socialLoginEnabled?: boolean | string;
  /** Ordered list of provider keys. Order IS the button order; unknown keys are ignored. */
  socialLoginProviders?: unknown;
  /** Public SSO client ids + the registered redirect URI (never secrets). */
  sso?: {
    google?: { clientId?: string };
    telegram?: { clientId?: string };
    /** The `redirect_uri` registered with the provider and posted back on the code exchange. */
    redirectUri?: string;
  };
}

/**
 * Narrowed, always-defined view of the SSO config (missing config => empty object). Reads the ambient
 * `window.config` without touching the global type: the widening cast is intentional and localized here.
 */
export function readSsoConfig(): SsoRuntimeConfig {
  if (typeof window === 'undefined') {
    return {};
  }
  const raw = (window as unknown as { config?: unknown }).config;
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  return raw as SsoRuntimeConfig;
}

/**
 * Is the master switch on? Honours the tenant string-flag convention: `''`, `'0'` and `'false'` are
 * OFF (a plain truthiness check would treat the non-empty string `'0'` as ON), everything else follows
 * normal truthiness.
 */
export function isMasterEnabled(config: SsoRuntimeConfig): boolean {
  const value = config.socialLoginEnabled;
  if (typeof value === 'string') {
    return value !== '' && value !== '0' && value.toLowerCase() !== 'false';
  }
  return Boolean(value);
}

/** Type guard: is `value` a recognised provider key? */
export function isSsoProvider(value: unknown): value is SsoProvider {
  return typeof value === 'string' && (RECOGNISED_PROVIDERS as readonly string[]).includes(value);
}

/**
 * Parse the raw `socialLoginProviders` config into an ordered, de-duplicated list of recognised
 * providers. Preserves config order (= button order), drops unknown keys (forward-compatible). Accepts
 * either an array OR a comma/space-separated string (helm/ini.js may deliver the list as a string, like
 * the sibling `socialLoginEnabled` flag convention); anything else yields `[]`.
 */
export function parseProviders(raw: unknown): SsoProvider[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(/[\s,]+/).filter(Boolean)
      : [];
  const seen = new Set<SsoProvider>();
  const out: SsoProvider[] = [];
  for (const key of list) {
    if (isSsoProvider(key) && !seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/**
 * The enabled, ordered providers for the current config: the master switch is on AND the provider is
 * listed AND everything its flow needs is configured — a public client id for both, PLUS the redirect
 * URI for Telegram (its full-page code flow cannot start without it). A provider missing any of these is
 * dropped, so the FE never shows a button that would either be rejected by the backend
 * (`SSO_PROVIDER_NOT_CONFIGURED`) or dead-click on a missing redirect URI.
 */
export function enabledProviders(config: SsoRuntimeConfig): SsoProvider[] {
  if (!isMasterEnabled(config)) {
    return [];
  }
  return parseProviders(config.socialLoginProviders).filter((provider) => {
    if (!providerClientId(config, provider)) {
      return false;
    }
    if (provider === 'telegram' && !ssoRedirectUri(config)) {
      return false;
    }
    return true;
  });
}

/** The public client id for a provider, or `undefined` when not configured. */
export function providerClientId(config: SsoRuntimeConfig, provider: SsoProvider): string | undefined {
  return config.sso?.[provider]?.clientId || undefined;
}

/** The registered redirect URI (used by the Telegram code flow), or `undefined`. */
export function ssoRedirectUri(config: SsoRuntimeConfig): string | undefined {
  return config.sso?.redirectUri || undefined;
}
